import React, { useEffect, useState } from "react";
import { AdminSupportChat } from "../messages/AdminSupportChat";
import { OverviewTab } from "./components/OverviewTab";
import { ExperienceTab } from "./components/ExperienceTab";
import { ClientCapacityTab } from "./components/ClientCapacityTab";
import { ReferencesTab } from "./components/ReferencesTab";
import { BackgroundChecksTab } from "./components/BackgroundChecksTab";
import { PackagesTab } from "./components/PackagesTab";
import { NotesTab } from "./components/NotesTab";
import { ReviewsTab } from "./components/ReviewsTab";
import { getGuide, updateGuideStatus, updateGuideBackgroundChecked, updateGuidePlatformFee } from "../../data/guidesService";
import { Guide, GuideStatus } from "../../types/guide";
import { getSupabaseClient } from "../../lib/supabase";
import GuideStatusBadge from "../../components/GuideStatusBadge";
import {
    backdrop,
    cardStyle,
    iconBtn,
    infoRowStyle,
    modal,
    mutedTextStyle,
    tabBarStyle,
    tabPanelStyle,
} from "./GuideDetailModal.styles";
import { formatCurrency, formatDate } from "./GuideDetailModal.utils";

const STATUS_OPTIONS: GuideStatus[] = [
    "pending",
    "consult_required",
    "not_fit",
    "approved",
    "pending_activation",
    "active",
];

const STATUS_LABELS: Record<GuideStatus, string> = {
    "pending": "Pending",
    "consult_required": "Consultation Required",
    "not_fit": "Not a Fit",
    "approved": "Approved",
    "pending_activation": "Pending Activation",
    "active": "Active",
};

const TAB_DEFS = [
    { id: "overview", label: "Overview" },
    { id: "experience", label: "Experience" },
    { id: "capacity", label: "Capacity" },
    { id: "references", label: "References" },
    { id: "background", label: "Background Checks" },
    { id: "packages", label: "Packages" },
    { id: "reviews", label: "Reviews" },
    { id: "notes", label: "Notes" },
];

const TAB_COMPONENTS: Record<string, React.ComponentType<{ guide: Guide }>> = {
    overview: OverviewTab,
    experience: ExperienceTab,
    capacity: ClientCapacityTab,
    references: ReferencesTab,
    background: BackgroundChecksTab,
    packages: PackagesTab,
    reviews: ReviewsTab,
    notes: NotesTab,
};

const PERFORMANCE_ORDER_STATUSES = ["paid_pending_delivery", "delivered"];

const normalizeInstallmentStatus = (status?: string | null) => {
    if (!status) return "scheduled";
    const normalized = status.toLowerCase();
    if (["paid", "succeeded", "completed"].includes(normalized)) return "paid";
    if (["scheduled", "pending", "pending_payment", "requires_payment_method", "active"].includes(normalized)) {
        return "scheduled";
    }
    if (["processing", "in_progress"].includes(normalized)) return "processing";
    if (["failed", "canceled", "cancelled", "overdue"].includes(normalized)) return "failed";
    return normalized;
};

async function fetchGuidePerformance(guideId: string) {
    const supabase = getSupabaseClient();
    const { data: orders } = await supabase
        .from("orders")
        .select("id, seeker_id, status, total_cents")
        .eq("guide_id", guideId)
        .in("status", PERFORMANCE_ORDER_STATUSES);

    if (!orders || orders.length === 0) {
        return { connectedSeekers: 0, incomeCents: 0 };
    }

    const orderIds = orders.map((order) => order.id).filter((id): id is string => !!id);
    const { data: installments } = orderIds.length
        ? await supabase
              .from("order_installments")
              .select("order_id, amount_cents, status")
              .in("order_id", orderIds)
        : { data: [], error: null };

    const installmentsTotalMap = new Map<string, number>();
    const installmentsPaidMap = new Map<string, number>();
    (installments ?? []).forEach((inst) => {
        if (!inst.order_id) return;
        const totalPrev = installmentsTotalMap.get(inst.order_id) ?? 0;
        installmentsTotalMap.set(inst.order_id, totalPrev + (inst.amount_cents ?? 0));

        if (normalizeInstallmentStatus(inst.status) === "paid") {
            const paidPrev = installmentsPaidMap.get(inst.order_id) ?? 0;
            installmentsPaidMap.set(inst.order_id, paidPrev + (inst.amount_cents ?? 0));
        }
    });

    const seekerSet = new Set<string>();
    let incomeCents = 0;

    orders.forEach((order) => {
        if (order.seeker_id) {
            seekerSet.add(order.seeker_id);
        }
        const totalCents = Number(order.total_cents ?? 0);
        const installmentsTotal = installmentsTotalMap.get(order.id) ?? 0;
        const depositCents = Math.max(totalCents - installmentsTotal, 0);
        const paidInstallments = installmentsPaidMap.get(order.id) ?? 0;
        const depositPaid = PERFORMANCE_ORDER_STATUSES.includes(order.status);
        incomeCents += (depositPaid ? depositCents : 0) + paidInstallments;
    });

    return { connectedSeekers: seekerSet.size, incomeCents };
}

interface GuideDetailModalProps {
    id: string;
    onClose: () => void;
    onUpdated: () => void;
}

export default function GuideDetailModal({ id, onClose, onUpdated }: GuideDetailModalProps) {
    const [guide, setGuide] = useState<Guide | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [next, setNext] = useState<GuideStatus>("pending");
    const [nextBackgroundChecked, setNextBackgroundChecked] = useState<boolean>(false);
    const [nextPlatformFee, setNextPlatformFee] = useState<string>("");
    const [activeTab, setActiveTab] = useState<string>("overview");
    const [showSupportChat, setShowSupportChat] = useState(false);
    const [performance, setPerformance] = useState<{ connectedSeekers: number; incomeCents: number }>({
        connectedSeekers: 0,
        incomeCents: 0,
    });

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        getGuide(id)
            .then((result) => {
                if (!mounted) return;
                setGuide(result);
                if (result) {
                    setNext(result.status);
                    setNextBackgroundChecked(result.backgroundChecked === true);
                    setNextPlatformFee(result.platformFeePercentage != null ? String(result.platformFeePercentage) : "");
                    setPerformance({
                        connectedSeekers: result.connectedSeekers ?? 0,
                        incomeCents: Math.round((result.income ?? 0) * 100),
                    });
                    fetchGuidePerformance(result.id)
                        .then((stats) => {
                            if (!mounted) return;
                            setPerformance(stats);
                        })
                        .catch((err) => {
                            console.error("Failed to load guide performance:", err);
                        });
                }
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, [id]);

    useEffect(() => {
        if (guide) setActiveTab("overview");
    }, [guide?.id]);

    async function onSave() {
        if (!guide) return;
        setSaving(true);
        setSaveError(null);

        try {
            const supabase = getSupabaseClient();

            // Update status if changed
            if (next !== guide.status) {
                await updateGuideStatus(guide.id, next);

                // Send notification to guide about status change
                try {
                    await supabase.functions.invoke('notify-guide-status-change', {
                        body: {
                            guide_id: guide.id,
                            new_status: next,
                        }
                    });
                } catch (notifyError) {
                    console.error('Failed to send guide notification:', notifyError);
                    // Don't fail the entire save if notification fails
                }
            }

            // Update background verification if changed
            const currentNormalized = guide.backgroundChecked === true;
            if (nextBackgroundChecked !== currentNormalized) {
                await updateGuideBackgroundChecked(guide.id, nextBackgroundChecked);
            }

            // Update platform fee if changed
            const newFee = nextPlatformFee.trim() === "" ? null : Number(nextPlatformFee);
            const currentFee = guide.platformFeePercentage ?? null;
            if (newFee !== currentFee) {
                await updateGuidePlatformFee(guide.id, newFee);
            }

            onUpdated();
        } catch (error) {
            console.error('Failed to save guide updates:', error);
            setSaveError(error instanceof Error ? error.message : 'Failed to save guide updates. Please try again.');
        } finally {
            setSaving(false);
        }
    }

    const displayGuide = guide
        ? {
              ...guide,
              connectedSeekers: performance.connectedSeekers,
              income: Number((performance.incomeCents / 100).toFixed(2)),
          }
        : null;
    const onboarding = displayGuide?.onboarding;
    const TabComponent = displayGuide ? TAB_COMPONENTS[activeTab] ?? OverviewTab : null;

    return (
        <div style={backdrop} onClick={onClose}>
            <div style={{ ...modal, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={(e) => e.stopPropagation()}>
                <header
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "24px 24px 0 24px",
                        marginBottom: 20,
                        gap: 12,
                        flexShrink: 0,
                    }}
                >
                    <h3
                        style={{
                            margin: 0,
                            fontSize: 24,
                            fontWeight: 600,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        Guide Details
                    </h3>
                    <button onClick={onClose} style={iconBtn} aria-label="Close">
                        X
                    </button>
                </header>

                {loading && <p style={{ padding: "0 24px" }}>Loading...</p>}
                {!loading && !guide && <p style={{ padding: "0 24px" }}>Guide not found.</p>}

                {!loading && guide && (
                    <>
                      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 24px' }}>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 24,
                                alignItems: "start",
                            }}
                        >
                            <div style={{ flex: "1 1 500px", minWidth: 0 }}>
                                <div style={tabBarStyle}>
                                    {TAB_DEFS.map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => setActiveTab(tab.id)}
                                            style={getTabButtonStyle(activeTab === tab.id)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                <div style={tabPanelStyle}>{TabComponent ? <TabComponent guide={displayGuide} /> : null}</div>
                            </div>

                            <aside style={{ display: "flex", flexDirection: "column", gap: 16, flex: "0 0 280px", width: 280 }}>
                                <div
                                    style={{
                                        border: "1px solid #2C2731",
                                        borderRadius: 16,
                                        padding: 16,
                                        background: "#F2EAC3",
                                        boxShadow: "inset 0 0 0 1px rgba(44,39,49,0.05)",
                                    }}
                                >
                                    <div style={{ fontWeight: 600, marginBottom: 12, color: "#2C2731" }}>
                                        Status &amp; Next Steps
                                    </div>
                                    <label
                                        style={{ fontSize: 13, color: "#403851", marginBottom: 6, display: "block" }}
                                    >
                                        Update status
                                    </label>
                                    <select
                                        value={next}
                                        onChange={(e) => setNext(e.target.value as GuideStatus)}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: 12,
                                            border: "1px solid #2C2731",
                                            background: "#FFFCEF",
                                            color: "#2C2731",
                                            marginBottom: 12,
                                            fontSize: 14,
                                        }}
                                    >
                                        {STATUS_OPTIONS.map((status) => (
                                            <option key={status} value={status}>
                                                {STATUS_LABELS[status]}
                                            </option>
                                        ))}
                                    </select>

                                    <label
                                        style={{ fontSize: 13, color: "#403851", marginBottom: 6, display: "block" }}
                                    >
                                        Background verification
                                    </label>
                                    <select
                                        value={nextBackgroundChecked === true ? "true" : "false"}
                                        onChange={(e) => {
                                            setNextBackgroundChecked(e.target.value === "true");
                                        }}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: 12,
                                            border: "1px solid #2C2731",
                                            background: "#FFFCEF",
                                            color: "#2C2731",
                                            marginBottom: 12,
                                            fontSize: 14,
                                        }}
                                    >
                                        <option value="false">No</option>
                                        <option value="true">Yes</option>
                                    </select>

                                    <label
                                        style={{ fontSize: 13, color: "#403851", marginBottom: 6, display: "block" }}
                                    >
                                        Platform fee %
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        placeholder="15 (default)"
                                        value={nextPlatformFee}
                                        onChange={(e) => setNextPlatformFee(e.target.value)}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: 12,
                                            border: "1px solid #2C2731",
                                            background: "#FFFCEF",
                                            color: "#2C2731",
                                            marginBottom: 12,
                                            fontSize: 14,
                                        }}
                                    />

                                    <div
                                        style={{
                                            borderTop: "1px solid rgba(44,39,49,0.12)",
                                            paddingTop: 12,
                                            marginTop: 12,
                                        }}
                                    >
                                        <div style={{ fontSize: 12, color: "#403851", marginBottom: 4 }}>Current status</div>
                                        <div style={{ marginBottom: 12 }}>
                                            <GuideStatusBadge status={guide.status} />
                                        </div>
                                        <div style={{ fontSize: 12, color: "#403851", marginTop: 8, marginBottom: 4 }}>
                                            Background verification
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: "#2C2731" }}>
                                            {displayGuide?.backgroundChecked === true ? "Yes" : "No"}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#403851", marginTop: 8, marginBottom: 4 }}>
                                            Platform fee
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: "#2C2731" }}>
                                            {displayGuide?.platformFeePercentage != null ? `${displayGuide.platformFeePercentage}%` : "15% (default)"}
                                        </div>
                                        <div style={mutedTextStyle}>Joined {formatDate(displayGuide?.joinedAt ?? "")}</div>
                                    </div>

                                    <button
                                        onClick={onSave}
                                        disabled={saving}
                                        style={{
                                            marginTop: 12,
                                            padding: "10px 14px",
                                            borderRadius: 12,
                                            border: "1px solid #2C2731",
                                            background: "#2C2731",
                                            color: "#FEF6D6",
                                            width: "100%",
                                            fontWeight: 600,
                                            letterSpacing: "0.01em",
                                            cursor: saving ? "progress" : "pointer",
                                        }}
                                    >
                                        {saving ? "Saving..." : "Update Status"}
                                    </button>
                                </div>

                                <div
                                    style={{
                                        border: "1px solid #2C2731",
                                        borderRadius: 16,
                                        padding: 16,
                                        background: "#FFFCEF",
                                        boxShadow: "inset 0 0 0 1px rgba(44,39,49,0.05)",
                                    }}
                                >
                                    <div style={{ fontWeight: 600, marginBottom: 12 }}>Performance</div>
                                    <div style={{ display: "grid", gap: 8, fontSize: 13, color: "#2C2731" }}>
                                        <div style={infoRowStyle}>
                                            <span>Connected Seekers</span>
                                            <span>{performance.connectedSeekers}</span>
                                        </div>
                                        <div style={infoRowStyle}>
                                            <span>Income Generated</span>
                                            <span>{formatCurrency(performance.incomeCents / 100)}</span>
                                        </div>
                                        <div style={infoRowStyle}>
                                            <span>Deals Closed Rate</span>
                                            <span>
                                                {displayGuide?.dealsClosedRate != null
                                                    ? `${(displayGuide.dealsClosedRate * 100).toFixed(1)}%`
                                                    : "N/A"}
                                            </span>
                                        </div>
                                        <div style={infoRowStyle}>
                                            <span>References</span>
                                            <span>{onboarding?.references ? onboarding.references.length : 0}</span>
                                        </div>
                                        <div style={infoRowStyle}>
                                            <span>Packages</span>
                                            <span>{onboarding?.packages ? onboarding.packages.length : 0}</span>
                                        </div>
                                    </div>
                                </div>

                                {/*<div
                                    style={{
                                        border: "1px solid #2C2731",
                                        borderRadius: 16,
                                        padding: 16,
                                        background: "#F2EAC3",
                                        boxShadow: "inset 0 0 0 1px rgba(44,39,49,0.05)",
                                    }}
                                >
                                    <div style={{ fontWeight: 600, marginBottom: 12 }}>Quick Actions</div>
                                    <div style={{ display: "grid", gap: 8 }}>
                                        <button
                                            style={{
                                                padding: "10px 12px",
                                                border: "1px solid #2C2731",
                                                borderRadius: 12,
                                                background: "#FFFCEF",
                                                color: "#2C2731",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Send Message
                                        </button>
                                        <button
                                            style={{
                                                padding: "10px 12px",
                                                border: "1px solid #2C2731",
                                                borderRadius: 12,
                                                background: "#FFFCEF",
                                                color: "#2C2731",
                                                fontWeight: 500,
                                            }}
                                        >
                                            View Reports
                                        </button>
                                    </div>
                                </div>*/}
                            </aside>
                        </div>
                      </div>

                        {saveError && (
                            <div style={{ padding: "8px 24px 0", color: "#d32f2f", fontSize: 14, textAlign: "right" }}>
                                {saveError}
                            </div>
                        )}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 12,
                                padding: "16px 24px",
                                borderTop: "1px solid rgba(44,39,49,0.15)",
                                flexShrink: 0,
                            }}
                        >
                            <button
                                onClick={() => setShowSupportChat(true)}
                                style={{
                                    padding: "10px 16px",
                                    border: "1px solid #2C2731",
                                    borderRadius: 12,
                                    background: "#FFFCEF",
                                    color: "#2C2731",
                                    fontWeight: 500,
                                }}
                            >
                                Message
                            </button>
                            <button
                                onClick={onClose}
                                style={{
                                    padding: "10px 16px",
                                    border: "1px solid #2C2731",
                                    borderRadius: 12,
                                    background: "#FFFCEF",
                                    color: "#2C2731",
                                    fontWeight: 500,
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onSave}
                                disabled={saving}
                                style={{
                                    padding: "10px 16px",
                                    borderRadius: 12,
                                    background: "#2C2731",
                                    color: "#FEF6D6",
                                    border: "1px solid #2C2731",
                                    fontWeight: 600,
                                    cursor: saving ? "progress" : "pointer",
                                }}
                            >
                                {saving ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </>
                )}

                {showSupportChat && guide && (
                    <div style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: 24,
                        overflow: "hidden",
                        zIndex: 10,
                        background: "#FFFCEF",
                    }}>
                        <AdminSupportChat
                            targetUserId={guide.id}
                            userName={guide.name}
                            userRole="guide"
                            onBack={() => setShowSupportChat(false)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function getTabButtonStyle(active: boolean): React.CSSProperties {
    return {
        padding: "8px 14px",
        borderRadius: 999,
        border: "1px solid",
        borderColor: active ? "#2C2731" : "rgba(44,39,49,0.25)",
        background: active ? "#2C2731" : "#F2EAC3",
        color: active ? "#FEF6D6" : "#2C2731",
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: "0.01em",
        cursor: "pointer",
        transition: "background 0.2s ease, color 0.2s ease, border-color 0.2s ease",
    };
}
